/**
 * Все оверлеи попадают в конец body в момент открытия: q-dialog — через портал
 * Quasar (createGlobalNode → body.appendChild), дропдауны и селекты — через
 * <Teleport to="body">. Значит последний совпавший в порядке DOM и есть верхний.
 *
 * Hover-дропдауны исключены намеренно: они открываются от наведения мыши и
 * ничего не ждут от клавиатуры, но, всплыв поверх открытого подтверждения,
 * иначе перехватывали бы у него звание верхнего оверлея и глушили ESC/Enter.
 * У .mosta-context-menu такого класса-маркера нет, поэтому hover-варианты этого
 * меню (они есть только в Analytics) в теории могут перехватить верхний уровень
 * у открытого подтверждения — на страницах с подтверждениями они не встречаются.
 *
 * .fmenu (меню инлайн-редактирования поля) в body не телепортируется, когда у
 * InlineDescriptor не выставлен moveToBody, но тогда он лежит внутри своего
 * оверлея-родителя и в порядке DOM всё равно идёт после него. А открытый на самой
 * странице .fmenu звание верхнего ни у кого не отнимает: диалог Quasar
 * добавляется в конец body, то есть заведомо после разметки страницы.
 */
const OVERLAY_SELECTOR =
    ".q-dialog, .dropdown-content:not(.dropdown-content--hover), .app-select__dropdown, .mosta-context-menu, .fmenu";

/**
 * Всё, что закрывается по Escape. Шире OVERLAY_SELECTOR: AppResizableDropdown
 * закрывает по Escape и hover-дропдауны, плюс попапы Quasar (.q-menu) и
 * ConfirmActionPopup. Все они лежат в DOM только пока открыты.
 */
const ESCAPE_CLOSABLE_SELECTOR = `${OVERLAY_SELECTOR}, .dropdown-content, .q-menu, .confirm-popup`;

/**
 * Открыто ли сейчас что-нибудь, что закрывается по Escape. Для обработчиков, которым
 * Escape достаётся, только когда закрывать больше нечего.
 */
export function hasEscapeClosableOverlay(): boolean {
    return !!document.querySelector(ESCAPE_CLOSABLE_SELECTOR);
}

/**
 * Лежит ли элемент в самом верхнем открытом оверлее. Так вложенные модалки,
 * дропдауны и селекты разбираются, чей сейчас Escape.
 */
export function isTopmostOverlay(el: HTMLElement | null): boolean {
    if (!el) {
        return false;
    }
    const overlays = document.querySelectorAll(OVERLAY_SELECTOR);
    const top = overlays[overlays.length - 1];
    return !!top && top.contains(el);
}

/**
 * Попапы q-select/q-date и меню подтверждения (QMenu) Quasar добавляет в конец body
 * в момент показа, поэтому стоящий в DOM после элемента открыт поверх него.
 */
function hasQuasarPopupAbove(el: HTMLElement): boolean {
    const popups = document.querySelectorAll(".q-menu");
    const last = popups[popups.length - 1];
    return !!last && !!(el.compareDocumentPosition(last) & Node.DOCUMENT_POSITION_FOLLOWING);
}

/**
 * Принадлежит ли Escape элементу: он лежит в верхнем оверлее и поверх него не открыт
 * попап Quasar. .q-menu в OVERLAY_SELECTOR нет, а закрывает его Quasar только на keyup,
 * так что обработчик на keydown иначе успел бы закрыть всё окно вместе с попапом.
 * Уступив, событие не глушить: тогда Quasar закроет свой попап сам.
 */
export function ownsEscape(el: HTMLElement | null): boolean {
    if (!el) {
        return false;
    }
    return isTopmostOverlay(el) && !hasQuasarPopupAbove(el);
}
